import { OptionPersistence } from './option.class';

/**
 * Prepares option data for `ref.update()` on `whoo/options/.../optionArray/{index}`.
 * The Firebase client rejects the whole write when any value is `undefined`
 * ("values argument contains undefined in property ..."), and the setters of OptionClass write
 * `undefined` straight into the data (multi-edit dialog with an empty, checked field - see
 * workorder-persist-option-undefined-2026-10-04).
 *
 *  - Top-level fields with value `undefined` become `null`. `update()` merges, so leaving the key
 *    out would keep the old database value although the owner emptied the field locally; `null`
 *    deletes the key and matches the local state.
 *  - Inside nested objects (`timePeriod`, `multipeChoice`) `undefined` values are removed. These
 *    nodes are replaced as a whole by `update()`, so leaving a key out deletes it. `undefined`
 *    array entries become `null` (same effect: the index is not written).
 *
 * Returns a new object; the input is not modified. Server values like
 * `firebase.database.ServerValue.TIMESTAMP` (`{ '.sv': 'timestamp' }`) pass through unchanged.
 */
export function normalizeOptionPersistence(data: OptionPersistence): OptionPersistence {
    if (!data) return data;
    const result: { [key: string]: unknown } = {};
    Object.keys(data).forEach(key => {
        const value = (<{ [key: string]: unknown }>data)[key];
        result[key] = value === undefined ? null : withoutUndefined(value);
    });
    return <OptionPersistence>result;
}

/** Recursively drops `undefined` values from plain objects and arrays; other values pass through. */
function withoutUndefined(value: unknown): unknown {
    if (Array.isArray(value)) {
        // Array.from also visits holes of a sparse array (as undefined)
        return Array.from(value, entry => entry === undefined ? null : withoutUndefined(entry));
    }
    if (isPlainObject(value)) {
        const result: { [key: string]: unknown } = {};
        Object.keys(value).forEach(key => {
            const entry = value[key];
            if (entry !== undefined) result[key] = withoutUndefined(entry);
        });
        return result;
    }
    return value;
}

function isPlainObject(value: unknown): value is { [key: string]: unknown } {
    if (value === null || typeof value !== 'object') return false;
    const prototype = Object.getPrototypeOf(value);
    return prototype === Object.prototype || prototype === null;
}
